
import _omit from 'lodash/omit'

import moment from 'moment-timezone'

import { mapState, mapGetters } from 'vuex'

import {
  ROUTE_URL_SURVEY,
  ROUTE_URL_REVIEW,
  ROUTE_URL_WAITLIST,
  ROUTE_PARAM_NAME_MERCHANT_SLUG,
  ROUTE_URL_NOT_FOUND,
  ROUTE_NAME_MODIFY_REVIEW,
} from '@/router'

import {
  formatDateLongWithYear,
  browserTimeZoneDiffersFromMerchant,
  formatTimeValues,
} from '@/helpers/dates'

import Loading from '@/components/Loading.vue'
import ButtonLoading from '@/components/ButtonLoading.jsx'

const RES_TYPE_SEARCH_BUTTON_TITLE =
  'Check other locations for this type of reservation'
const MAX_RES_SLOTS_NUMBER = 3

export default {
  props: {
    modify: Boolean,
  },
  components: {
    Loading,
    ButtonLoading,
  },
  data() {
    const maxResSlotsNumber = MAX_RES_SLOTS_NUMBER
    return {
      selectedIndex: -1,
      searchNearbyClicked: false,
      selectedResTypeName: null,
      maxResSlotsNumber,
    }
  },
  created() {
    if (!this.$store.state.api.merchant.reservationsEnabled) {
      if (!this.$store.state.api.merchant.waitlistEnabled) {
        this.$router.replace({ name: ROUTE_URL_NOT_FOUND })
      } else {
        this.$router.replace({ name: ROUTE_URL_WAITLIST })
      }
    }
  },
  updated() {
    if (this.shouldAutoSearchNearbyRestaurants) {
      this.searchNearbyRestaurants(false, null, -1)
    }
    if (this.inventoryItemsLoading) {
      this.searchNearbyClicked = false
      this.selectedResTypeName = null
      this.selectedIndex = -1
    }
  },
  watch: {
    inventoryItemsOtherLoading(newVal, oldVal) {
      if (!newVal && oldVal) {
        setTimeout(() => {
          const element = document.getElementsByClassName(
            'page--reservations'
          )[0]
          element.scrollIntoView({ behavior: 'smooth', block: 'end' })
        }, 50)
      }
    },
  },
  methods: {
    containSlot(inventoryType) {
      const search = this.$store.state.reservationSearch
      if (!search.time.value || !inventoryType.items.length) {
        return true
      }
      return inventoryType.items.some(
        item =>
          item.value[0] === search.time.value[0] &&
          item.value[1] === search.time.value[1]
      )
    },
    currency(reservationType) {
      return reservationType.currency
    },
    currencySymbol(reservationType) {
      return (0.0)
        .toLocaleString('en', {
          style: 'currency',
          currency: this.currency(reservationType),
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })
        .replace('0.00', '')
    },
    defaultPricePerCopy(inventoryType) {
      return `per ${inventoryType.price.perTable ? 'table' : 'person'}`
    },
    pricePerCopy(inventoryType) {
      return (
        this.merchant.reservationTypePriceCopy ||
        this.defaultPricePerCopy(inventoryType)
      )
    },
    async joinWaitlist() {
      this.$router.push({ name: ROUTE_URL_WAITLIST })
    },
    async reservationSlotSelected($event, time, inventoryItem) {
      if (!time.available) {
        return
      }

      this.$store.dispatch('reservationSearch/shallowUpdate', {
        time: {
          label: formatTimeValues(time.value[0], time.value[1]),
          value: time.value,
        },
      })

      await this.$store.dispatch('api/updateReservation', {
        previousReservationState: this.$store.state.api.reservation,
        ...this.$store.state.reservationSearch,
        time,
        waitlist: undefined,
        inventoryItem: _omit(inventoryItem, 'items'),
        id: this.modify ? this.$store.state.api.reservation.id : undefined,
        typeId: time.typeId,
      })

      if (inventoryItem.merchant) {
        this.$store.dispatch('api/clearInventoryOther')

        this.$router.push({
          name: inventoryItem.survey ? ROUTE_URL_SURVEY : ROUTE_URL_REVIEW,
          params: {
            [ROUTE_PARAM_NAME_MERCHANT_SLUG]: inventoryItem.merchant.slug,
          },
        })
      } else {
        this.$router.push({
          name: this.modify
            ? ROUTE_NAME_MODIFY_REVIEW
            : inventoryItem.survey
            ? ROUTE_URL_SURVEY
            : ROUTE_URL_REVIEW,
        })
      }
    },
    searchNearbyRestaurants(clicked, typeName, index) {
      this.selectedIndex = index
      const search = this.$store.state.reservationSearch
      if (!search.day || !search.time || !search.numberOfGuests) {
        return
      }
      if (clicked) {
        this.searchNearbyClicked = true
      }
      this.selectedResTypeName = typeName

      this.$store.dispatch('api/getInventoryOther', search)
    },
    shouldShowNearbySearchButton(inventoryType) {
      return (
        this.merchant.showNearbyResType &&
        this.inventoryItems.length > 1 &&
        inventoryType.items.length !== this.maxResSlotsNumber
      )
    },
  },
  computed: {
    ...mapState({
      inventoryItems: (state: any) => state.api.inventory.items || [],
      inventoryItemsOther: (state: any) =>
        (state.api.inventoryOther || {}).items || [],
      merchant: (state: any) => state.api.merchant,
      numberOfGuests: (state: any) => state.reservationSearch.numberOfGuests,
      day: (state: any) => state.reservationSearch.day,
      time: (state: any) => state.reservationSearch.time,
    }),
    ...mapGetters({
      reservationSearchValid: 'reservationSearch/valid',
      inventoryItemsLoading: 'api/inventoryLoading',
      inventoryItemsOtherLoading: 'api/inventoryOtherLoading',
      reservationOtherTimesAvailable: 'api/reservationOtherTimesAvailable',
      inventoryEmpty: 'api/inventoryEmpty',
      inventoryOtherEmpty: 'api/inventoryOtherEmpty',
      maxPartySizeExceeded: 'api/maxPartySizeExceeded',
    }),
    timeZone() {
      return browserTimeZoneDiffersFromMerchant()
        ? ` ${moment().format('z')}`
        : undefined
    },
    showSearchNearbyRestaurantsButton() {
      return (
        !this.inventoryItemsOther.length &&
        !this.modify &&
        this.merchant.hasOtherLocations &&
        this.merchant.showNearbyRestaurants &&
        !this.inventoryOtherEmpty
      )
    },
    currentDate() {
      return moment(this.day.value)
    },
    currentDateTime() {
      return moment(this.currentDate)
        .set('hour', this.time.value[0])
        .set('minute', this.time.value[1])
    },
    currentDateTimeFormatted() {
      return formatDateLongWithYear(this.currentDateTime)
    },
    showJoinWaitlistButton() {
      return (
        this.merchant.waitlistEnabled &&
        !this.modify &&
        this.currentDateTimeFormatted === formatDateLongWithYear(moment())
      )
    },
    inventoryNearbyItemsEmpty() {
      return (
        this.inventoryOtherEmpty ||
        !this.inventoryItemsOther.some(item => item.items.length)
      )
    },
    shouldAutoSearchNearbyRestaurants() {
      return (
        !this.inventoryItemsLoading &&
        !this.maxPartySizeExceeded &&
        !this.inventoryItemsOtherLoading &&
        this.merchant.autoSearchNearby &&
        this.showSearchNearbyRestaurantsButton &&
        this.inventoryItems.some(
          item => item.items.length !== this.maxResSlotsNumber
        ) &&
        this.inventoryItems.length === 1
      )
    },
    shouldShowNearbyUnavailableMessage() {
      return (
        ((!this.inventoryItemsOtherLoading &&
          this.inventoryOtherEmpty &&
          this.merchant.autoSearchNearby) ||
          this.searchNearbyClicked) &&
        !this.filteredNearbyInventory.length
      )
    },
    filteredNearbyInventory() {
      if (!this.inventoryItemsOther) {
        return
      }
      if (this.resTypeSearchSelected) {
        return this.inventoryItemsOther.filter(
          reservationType => this.selectedResTypeName === reservationType.title
        )
      }
      if (this.inventoryItems && this.inventoryItems.length === 1) {
        return this.inventoryItemsOther.filter(
          reservationType =>
            this.inventoryItems[0].title === reservationType.title
        )
      }
      return this.inventoryItemsOther.filter(
        reservationType => reservationType.isDefault
      )
    },
    resTypeSearchSelected() {
      return this.selectedResTypeName !== null
    },
  },
}
